import { useState, useContext } from "react";
import {
  Box,
  Grid,
  Stack,
  Table,
  Avatar,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import moment from "moment";
//Srcs
import "../../Style/pagestyle.scss";
import EmptyData from "../../Components/Include/EmptyData/EmptyData";
import StudentForm from "../../Components/Student/StudentForm";
import LoadingPage from "../../Utils/LoadingPage";
import StudentAction from "../../Components/Student/StudentAction";
import FooterPagination from "../../Utils/FooterPagination";
import useWithDimension from "../../Hooks/useWithDimension";
import useGetStudentPagination from "../../Hooks/useGetStudentPagination";
import { AuthContext } from "../../Context/AuthContext";
import { SearchTextField, ButtonCreate } from "../../Utils/Component";

export default function Student() {
  // Change Language
  const { t } = useContext(AuthContext);
  const { width } = useWithDimension();

  const [studentType, setStudentType] = useState("direct");

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const {
    page,
    limit,
    loading,
    refetch,
    setPage,
    tableData,
    setLoading,
    handleLimit,
    handleSearch,
    paginationData,
  } = useGetStudentPagination({ studentType });

  return (
    <div className="page-container">
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={6} sm={6} md={4} lg={3} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("search")}
            </Typography>
            <SearchTextField
              className={"text-field"}
              handleSearch={handleSearch}
            />
          </Grid>

          <Grid item xs={6} sm={6} md={4} lg={3} xl={2}>
            <Typography className={t("class-name-text-field-title")}>
              {t("thead_type")}
            </Typography>
            <FormControl fullWidth size="small" className={"text-field"}>
              <Select
                value={studentType}
                onChange={({ target }) => setStudentType(target?.value)}
              >
                <MenuItem value={"all"}>All</MenuItem>
                <MenuItem value={"direct"}>Direct</MenuItem>
                <MenuItem value={"online"}>Online</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12} md={4} lg={6} xl={8}>
            <ButtonCreate
              handleOpen={handleOpen}
              classBtn={"btn-create"}
              classIcon={"icon-add"}
            />

            {open && (
              <StudentForm
                open={open}
                handleClose={handleClose}
                setRefetch={refetch}
                dialogTitle={"Create"}
              />
            )}
          </Grid>
        </Grid>
      </Box>

      <Box className="body-container">
        <TableContainer sx={{ maxWidth: `${width}px` }}>
          <Table className="table" aria-label="simple table">
            <TableHead className="header-row ">
              <TableRow>
                <TableCell
                  className={t("class-name-header-title-start")}
                  width="5%"
                >
                  {t("no")}
                </TableCell>
                <TableCell className={t("class-name-header-title")} width="10%">
                  {t("thead_id")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_full_name")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_gender")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title")}
                  align="center"
                >
                  {t("thead_date_of_birth")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_start_school_date")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_telephone")}
                </TableCell>
                <TableCell className={t("class-name-header-title")}>
                  {t("thead_address")}
                </TableCell>
                <TableCell
                  className={t("class-name-header-title-end")}
                ></TableCell>
              </TableRow>
            </TableHead>
            <TableBody className="body">
              {loading ? (
                <TableRow className="body-row">
                  <TableCell
                    colSpan={10}
                    className="body-cell"
                    sx={{ bgcolor: "#fff" }}
                  >
                    <LoadingPage />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {tableData?.length === 0 ||
                  tableData?.length === undefined ? (
                    <TableRow className="body-row">
                      <TableCell colSpan={10} className="body-cell">
                        <EmptyData />
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData?.map((row, index) => {
                      return (
                        <TableRow className="body-row" key={index}>
                          <TableCell className="body-cell-start">
                            {index + paginationData?.slNo}
                          </TableCell>
                          <TableCell className="body-cell">
                            {row?.studentId}
                          </TableCell>
                          <TableCell className="body-cell">
                            <Stack direction="row" spacing={1.4}>
                              <Stack direction="column" justifyContent="center">
                                <Avatar
                                  sx={{ width: 40, height: 40 }}
                                  alt={row?.studentName}
                                  src={row?.profile}
                                />
                              </Stack>
                              <Stack direction="column" justifyContent="center">
                                <Typography className="student-name">
                                  {row?.familyName + " " + row?.studentName}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                          <TableCell className="body-cell">
                            {row?.gender === "Male"
                              ? t("thead_male")
                              : t("thead_female")}
                          </TableCell>
                          <TableCell className="body-cell" align="center">
                            {row?.dob && moment(row?.dob).format("DD-MMM-YYYY")}
                          </TableCell>
                          <TableCell className="body-cell">
                            {row?.startSchoolDate &&
                              moment(row?.startSchoolDate).format(
                                "DD-MMM-YYYY",
                              )}
                          </TableCell>
                          <TableCell className="body-cell">
                            {row?.phoneNumber}
                          </TableCell>
                          <TableCell className="body-cell">
                            {row?.address}
                          </TableCell>
                          <TableCell className="body-cell-end" align="right">
                            <StudentAction
                              editData={row}
                              setRefetch={refetch}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ==================== Pagination  ============================= */}
        <FooterPagination
          totalPages={paginationData?.totalPages}
          totalDocs={paginationData?.totalDocs}
          limit={limit}
          page={page}
          setPage={setPage}
          handleLimit={handleLimit}
          setLoading={setLoading}
        />
      </Box>
    </div>
  );
}
